import { alpha } from '@mui/material/styles';

// One chip system for the whole console, so role tags, plan badges, and status
// pills read identically everywhere. All chips: 22px tall, 12px text, 6px
// radius, weight 500 (never bold), soft-tint (alpha background of the hue +
// the hue itself as text) — readable in both light and dark, never a loud
// solid fill. Chips are BORDERLESS (filled tint only) — the console reserves
// colored borders for nothing; the accent lives on buttons. Pair with
// `variant="outlined"` on the <Chip> (the transparent border keeps sizing).

const BASE = { height: 22, fontSize: 12, borderRadius: '6px', fontWeight: 500 };

// Tinted badge of a palette tone (default: primary/rust). Use for plan badges
// ("Pro"), the "Per seat" label, and any held role tag. Filled tint, no border.
export const tagChipSx = (theme, tone = 'primary') => {
  const c = theme.palette[tone]?.main || theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';
  return {
    ...BASE,
    backgroundColor: alpha(c, dark ? 0.18 : 0.1),
    borderColor: 'transparent',
    color: c,
  };
};

// Faded outline for an inactive/neutral tag (e.g. "Member", a role not held).
export const mutedChipSx = (theme) => ({
  ...BASE,
  borderColor: theme.palette.divider,
  color: 'text.secondary',
  opacity: 0.8,
});

// Status pill. tone: 'success' | 'warning' | 'error' | 'neutral'. Neutral
// falls back to the muted treatment.
export const statusChipSx = (theme, tone = 'neutral') => {
  if (tone === 'neutral' || !theme.palette[tone]) return mutedChipSx(theme);
  return tagChipSx(theme, tone);
};

// Map an org/invoice status string to a tone for statusChipSx.
export const statusTone = (status) => {
  const v = (status || '').toLowerCase();
  if (v === 'active' || v === 'paid') return 'success';
  if (v === 'invited' || v === 'open' || v === 'pending' || v === 'past_due') return 'warning';
  return 'neutral';
};

// ---------------------------------------------------------------------------
// One card idiom for the whole console (the spec's flat-surface treatment):
// paper background, 10px radius, hairline border, no shadow. Interactive cards
// add the shared hover (hairline darkens, faint wash; never a lift) via
// consoleCardHoverSx. Standard content padding is 2.5 - pass padded: false for
// flush containers like tables.
export const consoleCardSx = (theme, { padded = true } = {}) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  ...(padded ? { p: 2.5 } : {}),
});

export const consoleCardHoverSx = (theme) => ({
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.text.primary, 0.2),
    backgroundColor: alpha(theme.palette.text.primary, 0.03),
  },
});

// Micro-label (table headers, stat labels): 11px, 600, uppercase, +0.06em.
export const microLabelSx = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'text.secondary',
};
